import { z } from "zod";
import { isStableFileUrl } from "../stable-file-url/stable-file-url.js";
import { documentCategorySchema } from "./document-categories.js";
import { glAccount, glAccountSchema } from "./gl-accounts.js";
import { incidentContainerSchema } from "./incident-containers.js";
/**
 * Entity extracted by AI (Heidelbot inbound contract).
 */
export const entitySchema = z
    .object({
    name: z.string(),
    value: z.union([z.string(), z.number()]),
    confidence: z.number(),
    displayName: z.string().optional(),
})
    .meta({
    description: 'Money entities ("totalCosts", "wage", "materialCosts", "paintWork", "costsOfRepair", "replacementValue", ' +
        '"remainingValue", "impairment", "lossOfUse", "totalNet", "totalTax", "totalGross") are integer cents. ' +
        '"mileage" is whole km, an integer.',
});
/**
 * Document category with confidence (Heidelbot inbound contract).
 *
 * Strict: `value` must be a canonical `documentCategory` enum value. Unknown
 * values fail validation (→ 400). See ADR-0003 for why no tolerance layer
 * exists at this boundary.
 */
export const categorySchema = z.object({
    value: documentCategorySchema,
    confidence: z.number(),
});
/** Money entity names captured per invoice position, integer cents. */
export const invoicePositionMoneyEntityNames = {
    NET: "net",
    TAX: "tax",
    GROSS: "gross",
};
/** Per-line-item ("position") entity names extracted by Heidelbot for the invoice workflow. */
export const invoicePositionEntityNames = {
    ...invoicePositionMoneyEntityNames,
    GL_ACCOUNT: "glAccount",
};
/**
 * A position (invoice line item) entity. Still a generic AI entity (net / tax / gross /
 * glAccount, candidates may repeat per name), but entities named "glAccount" must carry a
 * value from the {@link glAccountSchema} enum — an unknown GL account fails validation (→ 400).
 */
export const positionEntitySchema = entitySchema
    .superRefine((e, ctx) => {
    if (e.name === invoicePositionEntityNames.GL_ACCOUNT && !glAccountSchema.safeParse(e.value).success) {
        ctx.addIssue({ code: "custom", path: ["value"], message: `Unknown glAccount: ${String(e.value)}` });
    }
})
    .meta({
    description: `Position entity names: ${Object.values(invoicePositionMoneyEntityNames)
        .map((name) => `"${name}"`)
        .join(", ")} (money, integer cents) and "${invoicePositionEntityNames.GL_ACCOUNT}", whose value ` +
        `must be one of: ${Object.values(glAccount).join(", ")}. An unknown glAccount value is rejected with a 400.`,
});
/**
 * Attachment with extracted data from AI processing.
 */
export const attachmentSchema = z.object({
    fileName: z.string(),
    url: z.url().refine(isStableFileUrl, { message: "Attachment URL must be an s3-adapter Stable File URL" }).meta({
        description: "Stable File URL of the attachment (s3-adapter), resolved server-side at view time. Presigned S3 URLs are rejected.",
        example: "https://s3-adapter.api.stg.claimini.com/api/v1/bundles/FU04VaHIZI/RechnungRE881545172.pdf",
    }),
    mimeType: z.string(),
    summary: z.string().optional(),
    categories: z.array(categorySchema),
    entities: z.array(entitySchema),
    containers: z.array(incidentContainerSchema).optional().meta({
        description: "AI-suggested eClaim containers for this attachment. Omitted when Heidelbot has no suggestion.",
    }),
    positions: z
        .array(z.array(positionEntitySchema))
        .optional()
        .meta({ description: "Per-line-item extracted entities (net/tax/gross/glAccount), array of arrays. Invoice tasks only." }),
});
/** Highest-confidence entity by name; the first one wins a tie. Shared by the review-form prefill and the priority job. */
export function findHighestConfidenceEntity(entities, entityName) {
    const matches = entities.filter((e) => e.name === entityName);
    if (matches.length === 0)
        return undefined;
    return matches.reduce((best, current) => (current.confidence > best.confidence ? current : best));
}
/**
 * Returns the attachment whose categories contain the given `categoryValue`
 * with the highest confidence across all attachments. The match need not be the
 * attachment's own top category: a photo at 0.3 COST_ESTIMATE beats a PDF at 0.2,
 * even when the photo is 0.95 PHOTO. This differs from the invoice gate, which
 * compares each attachment's top categories. Returns `undefined` when no attachment matches.
 */
export function findHighestConfidenceAttachmentByCategory(attachments, categoryValue) {
    let bestAttachment;
    let bestConfidence = Number.NEGATIVE_INFINITY;
    for (const att of attachments) {
        for (const cat of att.categories) {
            if (cat.value === categoryValue && cat.confidence > bestConfidence) {
                bestConfidence = cat.confidence;
                bestAttachment = att;
            }
        }
    }
    return bestAttachment;
}
/** The entity naming the incident a task belongs to. */
export const CLAIMINI_ID_ENTITY_NAME = "claiminiId";
